'use es6';

import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { isLoading, isError } from 'reference-resolvers/utils';
import { MULTI_CURRENCY_INFORMATION } from 'reference-resolvers/constants/ReferenceObjectTypes';
import MultiCurrencyInformationResolver from 'reference-resolvers/resolvers/MultiCurrencyInformationResolver';
import ProvideReferenceResolvers from 'reference-resolvers/ProvideReferenceResolvers';
import ResolveReferences from 'reference-resolvers/ResolveReferences';
import PropTypes from 'prop-types';
import { Component } from 'react';
import * as ReferenceTypes from 'reference-resolvers/schema/ReferenceTypes';
import { jsx as _jsx } from "react/jsx-runtime";
class Currency extends Component {
  getShouldShowPlaceholder() {
    const {
      currencyCode,
      defaultCurrencyCode,
      value
    } = this.props;
    return !value && value !== 0 || !currencyCode && isLoading(defaultCurrencyCode);
  }
  getCurrencyCode() {
    const {
      currencyCode,
      defaultCurrencyCode
    } = this.props;
    if (currencyCode) {
      return currencyCode;
    }
    return isError(defaultCurrencyCode) ? defaultCurrencyCode : defaultCurrencyCode.label;
  }
  getCurrencyValue() {
    const {
      value
    } = this.props;
    // we assume that whatever value we're getting from the back-end is in the
    // format of numbers that can be parsed by js, i.e. they have no commas and
    // only one decimal (in regex terms /[\d+](\.[\d+])?/)
    // api change made to enforce this here: https://developers.hubspot.com/changelog/upcoming-api-number-value-changes
    return isNaN(value) ? null : +value;
  }
  renderCurrencyValue() {
    const {
      simplifyCurrency
    } = this.props;
    const currencyCode = this.getCurrencyCode();
    const currencyValue = this.getCurrencyValue();
    if (isError(currencyCode)) {
      return /*#__PURE__*/_jsx(FormattedIntlCurrency, {
        value: currencyValue
        // Intentionally hide the currency symbol when
        // we have an error loading the currency
        ,
        unit: ""
      });
    }

    // do not pass a unit into FormattedCurrency for formatting consistency purposes for more info see this PR:
    // https://git.hubteam.com/HubSpot/customer-data-objects-ui-components/pull/214
    return /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      currencyCode: currencyCode,
      "data-selenium-test": "currency-component-loaded",
      trailingZeroDisplay: simplifyCurrency ? 'stripIfInteger' : 'auto',
      value: currencyValue
    });
  }
  render() {
    const showPlaceholder = this.getShouldShowPlaceholder();
    return /*#__PURE__*/_jsx("span", {
      className: this.props.className,
      "data-selenium-test": showPlaceholder ? undefined : 'currency-component-loaded',
      "data-test-id": "currency-component",
      "data-default-currency-loading": isLoading(this.props.defaultCurrencyCode),
      children: showPlaceholder ? this.props.placeholder : this.renderCurrencyValue()
    });
  }
}
Currency.defaultProps = {
  placeholder: '-',
  simplifyCurrency: true
};
Currency.propTypes = {
  className: PropTypes.string,
  currencyCode: PropTypes.string,
  defaultCurrencyCode: ReferenceTypes.byId,
  placeholder: PropTypes.string,
  simplifyCurrency: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
const mapResolversToProps = resolvers => ({
  defaultCurrencyCode: resolvers[MULTI_CURRENCY_INFORMATION].byId('default')
});
const WrappedCurrency = ResolveReferences(mapResolversToProps)(Currency);

// need to provide here in case the resolvers aren't provided outside of this
// more info in post-revert PR after problem was caused by not providing the respolvers here: https://git.hubteam.com/HubSpot/customer-data-objects-ui-components/pull/133
export default ProvideReferenceResolvers({
  [MULTI_CURRENCY_INFORMATION]: MultiCurrencyInformationResolver
}, ResolveReferences(mapResolversToProps)(WrappedCurrency), {
  mergeResolvers: true
});
export { Currency as WrappedComponent };